// Pure draft helpers for the Import Wizard's review step - no React here.

let _localId = 0;
export const nextLocalId = () => `n${++_localId}`;

export const LETTERS = "ABCDEFGHIJ";

/** Same values the skill sheet builder stores on each step. */
export const VALIDATION_TYPES = ["Pass/Fail", "Observation"];

/** Fills in anything the test editor expects but a scanned question may lack (ids, True/False options). */
export function normalizeQuestion(q) {
  const base = { ...q, id: q.id || nextLocalId(), type: q.type || "MULTIPLE_CHOICE" };
  if (base.type === "TRUE_FALSE") {
    const tf = typeof base.tfCorrect === "boolean" ? base.tfCorrect : null;
    return {
      ...base,
      tfCorrect: tf,
      options: [
        { id: "true", text: "True", isCorrect: tf === true },
        { id: "false", text: "False", isCorrect: tf === false },
      ],
    };
  }
  if (base.type === "MATCHING") {
    return { ...base, pairs: (base.pairs || []).map((p) => ({ ...p, id: p.id || nextLocalId() })) };
  }
  if (base.type === "SHORT_ANSWER") {
    const { options: _options, ...rest } = base;
    return { ...rest, acceptedAnswers: Array.isArray(base.acceptedAnswers) ? base.acceptedAnswers.map(String) : [] };
  }
  return { ...base, options: (base.options || []).map((o) => ({ ...o, id: o.id || nextLocalId(), isCorrect: !!o.isCorrect })) };
}

/** Parses the JSON-encoded items/questions list out of a staged row's payload string. */
export function parseList(payloadString, type) {
  try {
    const payload = payloadString ? JSON.parse(payloadString) : {};
    const raw = payload[type === "SKILL_SHEET" ? "items" : "questions"];
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/** Turns a staged row (payload + JSON-encoded-string items/questions) into an editable draft. */
export function parseDraft(row) {
  let payload = {};
  try { payload = row.payload ? JSON.parse(row.payload) : {}; } catch { payload = {}; }
  const list = parseList(row.payload, row.type);
  return {
    rowId: row.id,
    fileName: row.fileName,
    type: row.type,
    isDuplicate: row.isDuplicate,
    include: !row.isDuplicate,
    dirty: false,
    payload,
    list: row.type === "SKILL_SHEET" ? list : list.map(normalizeQuestion),
  };
}

/** Reassembles a draft back into the payload shape the backend expects (items/questions re-stringified). */
export function serializeDraft(draft) {
  const listField = draft.type === "SKILL_SHEET" ? "items" : "questions";
  return { ...draft.payload, [listField]: JSON.stringify(draft.list) };
}

export function hasCorrectAnswer(q) {
  if (q.type === "TRUE_FALSE") return typeof q.tfCorrect === "boolean";
  if (q.type === "MATCHING") return (q.pairs || []).length > 0 && q.pairs.every((p) => p.prompt && p.answer);
  if (q.type === "SHORT_ANSWER") return (q.acceptedAnswers || []).some((a) => String(a).trim());
  return (q.options || []).some((o) => o.isCorrect);
}

/**
 * Required fields the AI couldn't fill in and the reviewer must — title, pass score, a non-empty
 * list, and (for tests) a correct answer on every question. Computed live off the draft so each
 * flag clears the instant the reviewer fixes it.
 */
export function computeMissingFields(draft) {
  const missing = [];
  if (!draft.payload.title || !String(draft.payload.title).trim()) missing.push("title");
  if (draft.payload.passingScore === "" || draft.payload.passingScore == null) missing.push("passingScore");
  if (!draft.list || draft.list.length === 0) missing.push(draft.type === "SKILL_SHEET" ? "items" : "questions");
  else if (draft.type !== "SKILL_SHEET" && draft.list.some((q) => !hasCorrectAnswer(q))) missing.push("answers");
  // the AI leaves validationType null when the source doesn't make Pass/Fail vs Observation clear
  if (draft.type === "SKILL_SHEET" && (draft.list || []).some((s) => !VALIDATION_TYPES.includes(s.validationType))) missing.push("validationType");
  return missing;
}

/** Items the AI flagged as uncertain that the reviewer hasn't confirmed or fixed yet. */
export function countUnresolvedFlags(draft) {
  return (draft.list || []).filter((s) => s.needsReview).length;
}

export function totalPoints(list, field) {
  return list.reduce((n, s) => n + (Number(s[field]) || 0), 0);
}

/** "✓ B", "✓ A, C", "✓ True", "3 pairs" - the collapsed-row answer summary. */
export function answerSummary(q) {
  if (q.type === "TRUE_FALSE") return typeof q.tfCorrect === "boolean" ? (q.tfCorrect ? "True" : "False") : null;
  if (q.type === "MATCHING") return `${(q.pairs || []).length} pairs`;
  if (q.type === "SHORT_ANSWER") {
    const accepted = (q.acceptedAnswers || []).map((a) => String(a).trim()).filter(Boolean);
    if (accepted.length === 0) return null;
    return accepted.length === 1 ? accepted[0] : `${accepted[0]} +${accepted.length - 1}`;
  }
  const letters = (q.options || []).map((o, i) => (o.isCorrect ? LETTERS[i] : null)).filter(Boolean);
  return letters.length ? letters.join(", ") : null;
}

export const TYPE_LABEL = {
  MULTIPLE_CHOICE: "multiple choice",
  MULTIPLE_SELECT: "multiple select",
  TRUE_FALSE: "true / false",
  MATCHING: "matching",
  SHORT_ANSWER: "short answer",
};
